import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiCheckCircle, FiTool, FiRefreshCw } from 'react-icons/fi';
import { releaseNotesData } from './releaseNotesData';
import { getVersionHistory } from '../../api/api';
import type { VersionHistoryItem } from '../../types/versionHistory.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ReleaseNotesModal({ isOpen, onClose }: Props) {
  const [releases, setReleases] = useState<VersionHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  const fetchReleases = async (force: boolean = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);

      const res: any = await getVersionHistory(force);
      if (res.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setReleases(res.data.data);
      } else {
        // Fallback to static data
        setReleases(releaseNotesData);
      }
    } catch (err) {
      console.warn('[ReleaseNotes] Failed to fetch dynamic release notes, using fallback', err);
      setReleases(releaseNotesData);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReleases(false);
    }
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />
      
      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-fade-in-up overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Product Updates</h2>
              <button
                onClick={() => fetchReleases(true)}
                disabled={refreshing || loading}
                title="Refresh release notes"
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors rounded-md"
              >
                <FiRefreshCw size={14} className={refreshing ? 'animate-spin text-[#B08D55]' : ''} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1">See what's new and improved in the Dealer Portal</p>
          </div>

          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-white rounded-full transition-colors shadow-sm bg-gray-100 border border-gray-200"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content (Scrollable) */}
        <div className="p-6 overflow-y-auto space-y-10 flex-1 custom-scrollbar bg-white">
          {loading ? (
            <div className="space-y-6 animate-pulse">
              {[1, 2].map((i) => (
                <div key={i} className="flex gap-6">
                  <div className="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-1/3" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-16 bg-gray-100 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : releases.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>No release notes available.</p>
            </div>
          ) : (
            releases.map((release, index) => {
              const displayDate = formatDate(release.releaseDate || release.date);
              const changesList = release.changes || [];
              const featuresList = release.features || [];
              const fixesList = release.fixes || [];

              return (
                <div key={release.id || release.version || index} className={`relative ${index !== 0 ? 'opacity-90' : ''}`}>
                  {/* Timeline line */}
                  {index !== releases.length - 1 && (
                    <div className="absolute left-[11px] top-8 bottom-[-40px] w-0.5 bg-gray-100" />
                  )}
                  
                  <div className="flex gap-6">
                    {/* Version Node */}
                    <div className="flex flex-col items-center mt-1 z-10 shrink-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-[3px] border-white shadow-sm ${index === 0 ? 'bg-[#B08D55] text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {index === 0 ? <FiCheckCircle size={10} /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />}
                      </div>
                    </div>

                    {/* Release Content */}
                    <div className="flex-1 pb-2">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-bold text-gray-900">{release.version}</h3>
                        {displayDate && (
                          <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{displayDate}</span>
                        )}
                        {index === 0 && (
                          <span className="text-[10px] uppercase tracking-wider font-extrabold bg-[#B08D55]/10 text-[#B08D55] px-2 py-0.5 rounded-full border border-[#B08D55]/20">
                            Current Version
                          </span>
                        )}
                      </div>
                      <p className="text-base font-bold text-[#B08D55] mb-2">{release.title}</p>

                      {release.summary && (
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                          {release.summary}
                        </p>
                      )}

                      <div className="space-y-4">
                        {/* If API returned 'changes' array */}
                        {changesList.length > 0 && (
                          <div>
                            <h4 className="text-xs uppercase tracking-wider font-bold text-gray-800 mb-2.5 flex items-center gap-2 bg-amber-50/80 w-fit px-2 py-1 rounded text-amber-800">
                              ✨ Highlights & Updates
                            </h4>
                            <ul className="space-y-2">
                              {changesList.map((item, i) => (
                                <li key={i} className="text-sm text-gray-600 flex items-start gap-2.5 leading-relaxed">
                                  <span className="text-[#B08D55] mt-1 shrink-0">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* If legacy 'features' array exists */}
                        {featuresList.length > 0 && (
                          <div>
                            <h4 className="text-xs uppercase tracking-wider font-bold text-gray-800 mb-2.5 flex items-center gap-2 bg-emerald-50 w-fit px-2 py-1 rounded text-emerald-700">
                              ✨ New Features
                            </h4>
                            <ul className="space-y-2">
                              {featuresList.map((item, i) => (
                                <li key={i} className="text-sm text-gray-600 flex items-start gap-2.5 leading-relaxed">
                                  <span className="text-emerald-500 mt-1 shrink-0">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* If legacy 'fixes' array exists */}
                        {fixesList.length > 0 && (
                          <div>
                            <h4 className="text-xs uppercase tracking-wider font-bold text-gray-800 mb-2.5 flex items-center gap-2 bg-blue-50 w-fit px-2 py-1 rounded text-blue-700">
                              <FiTool size={12} /> Improvements & Fixes
                            </h4>
                            <ul className="space-y-2">
                              {fixesList.map((item, i) => (
                                <li key={i} className="text-sm text-gray-600 flex items-start gap-2.5 leading-relaxed">
                                  <span className="text-blue-500 mt-1 shrink-0">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
